import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { useRxDatabase } from 'rxdb/plugins/react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { entityConfigs } from '@ast-data/shared';
import DialogFooterBar from './DialogFooterBar';
import { ExpandableSection } from './ExpandableSection';
import { APP_BAR_TITLE_SX } from './appBarTitle';
import { useSyncErrors } from '../features/sync/useSyncErrors';
import type { SyncStatus } from '../features/sync/useSyncStatus';
import { useOnlineStatus } from '../features/sync/useOnlineStatus';
import { buildSyncReport, copyToClipboard } from '../features/sync/syncReport';
import {
    deletedLocally,
    dismissRollbacks,
    markRollbacksSeen,
    pruneRollbacks,
    rolledBackChanges,
    type PushRejection
} from '../features/sync/pushRejections';
import {
    checkPendingQueue,
    compareWithServer,
    readPendingQueue,
    type PendingComparison,
    type PendingDoc,
    type QueueCheckResult
} from '../features/sync/pendingQueue';
import { useAuth } from '../features/auth/useAuth';
import { useActorLabel } from '../features/users/useActorLabel';
import {
    DataGridPro,
    type GridColDef,
    type GridListViewColDef
} from '../lib/dataGrid';
import type { AppDatabase } from '../db/database';
import { isStreamConnected, resyncAllReplications } from '../db/replication';
import { formatDateTime } from '../utils/formatDate';
import { deviceLines, startupLines } from '../features/support/diagnostics';

/**
 * What the sync is doing, in the words the app and the server actually used —
 * what the status chip opens, in every state. Shows the queue document by
 * document, then the failures, then everything a report needs as one block of
 * text made to be pasted into a message.
 *
 * The block is offered as selectable text, not only as a clipboard button: the
 * field app is routinely opened outside a secure context, where the clipboard
 * API does not exist at all (see `copyToClipboard`).
 */
export default function SyncDetailsDialog({
    open,
    onClose,
    status
}: {
    open: boolean;
    onClose: () => void;
    /**
     * Handed in rather than read again here: each `useSyncStatus` counts the
     * queue from ITS OWN mount, so a second instance opened alongside the chip
     * would report an empty queue.
     */
    status: SyncStatus;
}) {
    const intl = useIntl();
    const theme = useTheme();
    // On a phone this is the whole screen's worth of text anyway - a dialog
    // with margins around it would only make the queue harder to read.
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();
    const online = useOnlineStatus();
    const db = useRxDatabase() as unknown as AppDatabase | undefined;
    const actorLabel = useActorLabel();
    const { pending, blockedBy, replicatingHere } = status;
    const errors = useSyncErrors();
    const [queue, setQueue] = useState<PendingDoc[]>([]);
    const [rolledBack, setRolledBack] = useState<PushRejection[]>([]);
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState<QueueCheckResult | null>(null);
    const [comparing, setComparing] = useState(false);
    const [comparison, setComparison] = useState<Map<
        string,
        PendingComparison
    > | null>(null);
    const [copied, setCopied] = useState<boolean | null>(null);
    const [retried, setRetried] = useState(false);

    const lastReadAt = useRef(0);

    /**
     * Re-read whenever the queue's own count moves: the journal is shared by
     * every window, so it changes under this list unasked. At most once a
     * second, though: during a large upload the count moves many times a
     * second, and each read looks up every queued document.
     */
    useEffect(() => {
        if (!open || !db) {
            return;
        }
        let current = true;
        const wait = Math.max(
            0,
            lastReadAt.current + READ_INTERVAL_MS - Date.now()
        );
        const timer = setTimeout(() => {
            lastReadAt.current = Date.now();
            void readPendingQueue(db).then((docs) => {
                if (!current) {
                    return;
                }
                setQueue(docs);
                // Reading them here IS the acknowledgement; the record stays.
                setRolledBack(rolledBackChanges());
                markRollbacksSeen();
            });
        }, wait);
        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [open, db, pending, checked]);

    /**
     * Once per opening, and apart from the queue: a lookup that fails must not
     * hold the queue up. What was deleted since has nothing left to enter.
     */
    useEffect(() => {
        if (!open || !db) {
            return;
        }
        let current = true;
        void pruneRollbacks(deletedLocally(db))
            .catch((error: unknown) => {
                console.warn('[sync] could not tidy dropped changes:', error);
            })
            .then(() => {
                if (current) {
                    setRolledBack(rolledBackChanges());
                }
            });
        return () => {
            current = false;
        };
    }, [open, db]);

    // Both chromes keep this mounted, so it resets itself on the way out —
    // reopening must not still show the last visit's answer.
    function close() {
        setCopied(null);
        setRetried(false);
        setChecked(null);
        setComparison(null);
        onClose();
    }

    // A line per queued document: built when it is shown or copied, not on
    // every render of a dialog that may hold thousands.
    const buildReport = () =>
        buildSyncReport(
            {
                user: user ? `${user.displayName} (${user.username})` : '',
                online,
                pending,
                blockedBy
            },
            errors,
            queue,
            comparison
        );

    // Could not copy: the text is right beside the button to select instead.
    async function copy() {
        setCopied(await copyToClipboard(buildReport()));
    }

    function retry() {
        resyncAllReplications();
        setRetried(true);
    }

    /**
     * Nothing a new attempt could fix: the queue is empty, nothing failed, and
     * the live stream is up. Pressing it then starts a full pull that changes
     * nothing anybody can see - and "Ein neuer Versuch läuft" over an empty
     * queue reads like a repair that was needed.
     *
     * A dropped stream keeps it enabled even with an empty queue: that is the
     * one case where a resync really does fetch something back (see the
     * RESYNC on reconnect in db/replication).
     */
    const nothingToRetry =
        pending === 0 && errors.length === 0 && isStreamConnected();

    async function compare() {
        setComparing(true);
        try {
            setComparison(await compareWithServer(queue));
        } finally {
            setComparing(false);
        }
    }

    async function check() {
        if (!db) {
            return;
        }
        setChecking(true);
        try {
            setChecked(await checkPendingQueue(db, online));
        } finally {
            setChecking(false);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
        >
            {/* The same head the record editors wear: title left, the way out
                right, ruled off from the body (see FormDialogAppBar). */}
            <AppBar
                position="relative"
                color="inherit"
                elevation={0}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Toolbar sx={{ gap: 1 }}>
                    <Typography
                        variant="h6"
                        noWrap
                        sx={{ ...APP_BAR_TITLE_SX, flex: 1, minWidth: 0 }}
                    >
                        <FormattedMessage
                            id={
                                errors.length > 0
                                    ? 'syncError.title'
                                    : 'sync.title'
                            }
                        />
                    </Typography>
                    <IconButton
                        edge="end"
                        onClick={close}
                        aria-label={intl.formatMessage({ id: 'common.close' })}
                    >
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    <FormattedMessage
                        id={
                            errors.length > 0
                                ? 'syncError.explain'
                                : 'sync.explain'
                        }
                    />
                </DialogContentText>

                <StatusLines
                    online={online}
                    replicatingHere={replicatingHere}
                    blockedBy={blockedBy}
                />

                <RolledBackSection
                    changes={rolledBack}
                    onDismiss={() => {
                        dismissRollbacks(rolledBack);
                        setRolledBack(rolledBackChanges());
                    }}
                />

                <QueueSection
                    queue={queue}
                    checking={checking}
                    checked={checked}
                    canCheck={Boolean(db) && queue.length > 0}
                    onCheck={check}
                    comparing={comparing}
                    comparison={comparison}
                    onCompare={compare}
                    canCompare={online && queue.length > 0}
                    actorLabel={actorLabel}
                />

                {errors.length > 0 && (
                    <ExpandableSection
                        title={<FormattedMessage id="sync.errorsTitle" />}
                        counts={<Chip size="small" label={errors.length} />}
                        // What this dialog is opened for, so open.
                        defaultExpanded
                    >
                        <Stack spacing={1.5}>
                            {errors.map((error) => (
                                <Paper
                                    key={error.id}
                                    variant="outlined"
                                    sx={{ p: 1.5 }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            mb: 0.5
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            label={
                                                error.direction
                                                    ? `${error.collection} · ${error.direction}`
                                                    : error.collection
                                            }
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {formatDateTime(error.lastAt)}
                                        </Typography>
                                        {error.count > 1 && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <FormattedMessage
                                                    id="syncError.occurrences"
                                                    values={{
                                                        count: error.count,
                                                        first: formatDateTime(
                                                            error.firstAt
                                                        )
                                                    }}
                                                />
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {error.message}
                                    </Typography>
                                    {error.details.map((detail, index) => (
                                        <Typography
                                            key={index}
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontFamily: 'monospace',
                                                color: 'text.secondary',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {detail}
                                        </Typography>
                                    ))}
                                </Paper>
                            ))}
                        </Stack>
                    </ExpandableSection>
                )}

                <StartupSection />

                {retried && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage id="syncError.retried" />
                    </Alert>
                )}

                <ExpandableSection
                    title={<FormattedMessage id="syncError.reportLabel" />}
                    // The text field scrolls itself.
                    maxHeight={null}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={copy}
                        sx={{ mb: 2 }}
                    >
                        <FormattedMessage id="syncError.copy" />
                    </Button>
                    {copied === true && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <FormattedMessage id="syncError.copied" />
                        </Alert>
                    )}
                    {copied === false && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <FormattedMessage id="syncError.copyFailed" />
                        </Alert>
                    )}
                    <ReportText build={buildReport} />
                </ExpandableSection>
            </DialogContent>
            <DialogFooterBar>
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={close}
                >
                    <FormattedMessage id="common.close" />
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    disabled={!online || nothingToRetry}
                    onClick={retry}
                >
                    <FormattedMessage id="syncError.retry" />
                </Button>
            </DialogFooterBar>
        </Dialog>
    );
}

/** How often an open dialog re-reads the queue while it is moving. */
const READ_INTERVAL_MS = 1000;

/** The queue grid's cap; past it the list scrolls inside, virtualized. */
const QUEUE_MAX_HEIGHT = 420;

/**
 * The report as text to select by hand. Only rendered while its section is
 * open — it carries a line per queued document, so it is not built otherwise.
 */
function ReportText({ build }: { build: () => string }) {
    const intl = useIntl();
    const report = build();
    return (
        <TextField
            label={intl.formatMessage({ id: 'syncError.reportLabel' })}
            value={report}
            multiline
            minRows={6}
            maxRows={14}
            fullWidth
            slotProps={{
                htmlInput: {
                    readOnly: true,
                    style: { fontFamily: 'monospace', fontSize: '0.75rem' }
                }
            }}
        />
    );
}

/**
 * How long this start took, collection by collection — the same lines a fault
 * report carries, but on every device and without waiting for a hang. Closed
 * by default: it is for whoever looks into a slow device, not for everybody
 * checking their sync. Not translated, like the report.
 */
function StartupSection() {
    // Read once per opening: the figures describe a start that is over.
    const lines = useMemo(() => startupLines(), []);
    const [copied, setCopied] = useState<boolean | null>(null);
    if (lines.length === 0) {
        return null;
    }
    // With the device lines on top: timings without version and browser say
    // nothing to whoever receives them.
    async function copy() {
        setCopied(
            await copyToClipboard(
                ['AST Data — Startzeiten', ...deviceLines(), '', ...lines].join(
                    '\n'
                )
            )
        );
    }
    return (
        <ExpandableSection title={<FormattedMessage id="sync.startupTitle" />}>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={copy}
                sx={{ mb: 2 }}
            >
                <FormattedMessage id="sync.startupCopy" />
            </Button>
            {copied === true && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <FormattedMessage id="sync.startupCopied" />
                </Alert>
            )}
            {copied === false && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <FormattedMessage id="syncError.copyFailed" />
                </Alert>
            )}
            {lines.map((line, index) => (
                <Typography
                    key={index}
                    variant="caption"
                    component="div"
                    sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {line}
                </Typography>
            ))}
        </ExpandableSection>
    );
}

function SectionHeading({ id }: { id: string }) {
    return (
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            <FormattedMessage id={id} />
        </Typography>
    );
}

/**
 * Where this device stands: whether the connection is up, whether the live
 * stream behind it is (a device that pushes and pulls but has lost the stream
 * sees changes late and has no other way to tell), whether THIS window is the
 * one syncing, and who the queue is waiting for.
 */
function StatusLines({
    online,
    replicatingHere,
    blockedBy
}: {
    online: boolean;
    replicatingHere: boolean;
    blockedBy: string[];
}) {
    const connection = !online
        ? 'sync.stateOffline'
        : isStreamConnected()
          ? 'sync.stateOnline'
          : 'sync.stateNoStream';

    return (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="body2">
                <FormattedMessage id={connection} />
            </Typography>
            <Typography variant="body2">
                <FormattedMessage
                    id={
                        replicatingHere
                            ? 'sync.thisWindow'
                            : 'layout.sync.otherWindow'
                    }
                />
            </Typography>
            {blockedBy.length > 0 && (
                <Typography variant="body2">
                    <FormattedMessage
                        id="layout.sync.blocked"
                        values={{ names: blockedBy.join(', ') }}
                    />
                </Typography>
            )}
        </Stack>
    );
}

function entityLabel(intl: IntlShape, collection: string): string {
    const name = entityNameByCollection.get(collection);
    return name
        ? intl.formatMessage({
              id: `conflict.entity.${name}`,
              defaultMessage: collection
          })
        : collection;
}

/** Entity name per RxDB collection. */
const entityNameByCollection = new Map(
    entityConfigs.map((entity) => [entity.collection, entity.name])
);

/**
 * The queue itself, one row per document — and the check that can settle a
 * count that will not go down (see features/sync/pendingQueue for what that
 * check is allowed to conclude).
 */
function QueueSection({
    queue,
    checking,
    checked,
    canCheck,
    onCheck,
    comparing,
    comparison,
    onCompare,
    canCompare,
    actorLabel
}: {
    queue: PendingDoc[];
    checking: boolean;
    checked: QueueCheckResult | null;
    canCheck: boolean;
    onCheck: () => void;
    comparing: boolean;
    comparison: Map<string, PendingComparison> | null;
    onCompare: () => void;
    canCompare: boolean;
    actorLabel: (userId?: string) => string;
}) {
    const intl = useIntl();

    const label = useCallback(
        (doc: PendingDoc) => entityLabel(intl, doc.collection),
        [intl]
    );

    return (
        <>
            <SectionHeading id="sync.queueTitle" />
            {queue.length === 0 ? (
                <Alert severity="success">
                    <FormattedMessage id="sync.queueEmpty" />
                </Alert>
            ) : (
                <QueueList
                    queue={queue}
                    label={label}
                    comparison={comparison}
                    actorLabel={actorLabel}
                />
            )}

            {checked && (
                <Alert
                    severity={
                        checked.frozen
                            ? 'warning'
                            : checked.removed > 0
                              ? 'success'
                              : 'info'
                    }
                    sx={{ mt: 1.5 }}
                >
                    <FormattedMessage
                        id={
                            // A journal the browser will not store cannot be
                            // repaired at all, so it is said before anything
                            // else.
                            checked.frozen
                                ? 'sync.journalFrozen'
                                : checked.removed > 0
                                  ? 'sync.checkRemoved'
                                  : `sync.checkClean.${checked.outcome}`
                        }
                        values={{
                            count: checked.removed,
                            remaining: checked.remaining
                        }}
                    />
                </Alert>
            )}

            {canCheck && (
                <Box sx={{ mt: 1.5 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: 'wrap' }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                checking ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <FactCheckIcon />
                                )
                            }
                            disabled={checking}
                            onClick={onCheck}
                        >
                            <FormattedMessage
                                id={checking ? 'sync.checking' : 'sync.check'}
                            />
                        </Button>
                        {/* The diagnosis, for a queue holding documents that
                            ought to have gone up long ago: it asks the server
                            what it actually has for each of them. */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                comparing ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <CompareArrowsIcon />
                                )
                            }
                            disabled={comparing || !canCompare}
                            onClick={onCompare}
                        >
                            <FormattedMessage
                                id={
                                    comparing
                                        ? 'sync.comparing'
                                        : 'sync.compare'
                                }
                            />
                        </Button>
                    </Stack>
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{ mt: 0.5, color: 'text.secondary' }}
                    >
                        <FormattedMessage id="sync.checkExplain" />
                    </Typography>
                </Box>
            )}
        </>
    );
}

type QueueRow = PendingDoc & { id: string };

/**
 * The queued documents as a virtualized list: after a large import the queue
 * holds thousands, and a card per document drawn at once made the dialog
 * crawl. The grid draws only the rows in view.
 */
function QueueList({
    queue,
    label,
    comparison,
    actorLabel
}: {
    queue: PendingDoc[];
    label: (doc: PendingDoc) => string;
    comparison: Map<string, PendingComparison> | null;
    actorLabel: (userId?: string) => string;
}) {
    const rows = useMemo<QueueRow[]>(
        () =>
            queue.map((doc) => ({
                ...doc,
                id: `${doc.collection}/${doc.docId}`
            })),
        [queue]
    );
    const listViewColumn = useMemo<GridListViewColDef<QueueRow>>(
        () => ({
            field: 'entry',
            renderCell: ({ row }) => (
                <QueueEntry
                    doc={row}
                    label={label(row)}
                    author={actorLabel(row.userId)}
                    verdict={comparison?.get(row.id)}
                />
            )
        }),
        [label, actorLabel, comparison]
    );
    return (
        // Grows with its rows up to the cap, then scrolls inside.
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: QUEUE_MAX_HEIGHT
            }}
        >
            <DataGridPro
                rows={rows}
                columns={QUEUE_COLUMNS}
                listView
                listViewColumn={listViewColumn}
                getRowHeight={autoRowHeight}
                getEstimatedRowHeight={estimatedRowHeight}
                disableRowSelectionOnClick
                hideFooter
                sx={{
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                        { outline: 'none' }
                }}
            />
        </Box>
    );
}

// The list view draws only `listViewColumn`; the grid still wants a column.
const QUEUE_COLUMNS: GridColDef<QueueRow>[] = [{ field: 'docId' }];
const autoRowHeight = () => 'auto' as const;
const estimatedRowHeight = () => 84;

/** One queued document: what it is, its state, and what the server said. */
function QueueEntry({
    doc,
    label,
    author,
    verdict
}: {
    doc: PendingDoc;
    label: string;
    author: string;
    verdict?: PendingComparison;
}) {
    return (
        <Box sx={{ py: 1.25, width: '100%', lineHeight: 'normal' }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
            >
                <Chip size="small" label={label} />
                {doc.updatedAt !== '' && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                    >
                        {formatDateTime(doc.updatedAt)}
                    </Typography>
                )}
                {author !== '' && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage
                            id="sync.queueAuthor"
                            values={{ name: author }}
                        />
                    </Typography>
                )}
            </Stack>
            <Typography variant="body2">
                <FormattedMessage id={`sync.queueState.${doc.state}`} />
            </Typography>
            {doc.rejected && (
                <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'warning.main' }}
                >
                    <FormattedMessage
                        id="sync.queueRejected"
                        values={{
                            count: doc.rejected.count,
                            at: formatDateTime(doc.rejected.lastAt)
                        }}
                    />
                </Typography>
            )}
            <ServerVerdict verdict={verdict} />
            <Typography
                variant="caption"
                component="div"
                sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    wordBreak: 'break-all'
                }}
            >
                {doc.collection} · {doc.docId}
            </Typography>
        </Box>
    );
}

/**
 * What the server holds for one queued document. "Der Server hat genau diesen
 * Stand" says the change DID arrive and only the note stayed behind — a
 * different problem from "der Server hat einen anderen Stand", where the change
 * never got there while the app believed otherwise.
 */
function ServerVerdict({ verdict }: { verdict?: PendingComparison }) {
    if (!verdict) {
        return null;
    }
    return (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
            <FormattedMessage
                id={`sync.compared.${verdict.result}`}
                values={{ fields: verdict.differing.join(', ') }}
            />
        </Typography>
    );
}

/**
 * Changes the server refused, which were therefore dropped. Kept apart from the
 * queue: a queued document is still on its way, this one is gone and somebody
 * has to re-enter it.
 */
function RolledBackSection({
    changes,
    onDismiss
}: {
    changes: PushRejection[];
    /** Entered again or not needed: the whole list goes. */
    onDismiss: () => void;
}) {
    const intl = useIntl();
    if (changes.length === 0) {
        return null;
    }
    return (
        <>
            <SectionHeading id="sync.rolledBackTitle" />
            <Alert
                severity="warning"
                action={
                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={onDismiss}
                    >
                        <FormattedMessage id="sync.rolledBackDismiss" />
                    </Button>
                }
            >
                <FormattedMessage id="sync.rolledBackExplain" />
            </Alert>
            <Stack spacing={1} sx={{ mt: 1 }}>
                {changes.map((change) => (
                    <Paper
                        key={`${change.collection}/${change.docId}`}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                            <Chip
                                size="small"
                                label={entityLabel(intl, change.collection)}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                {formatDateTime(change.lastAt)}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            component="div"
                            sx={{
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                                wordBreak: 'break-all'
                            }}
                        >
                            {change.collection} · {change.docId}
                        </Typography>
                    </Paper>
                ))}
            </Stack>
        </>
    );
}
